/**
 * Unterseite „Vom neuen Lead zur Bestellung" (/auswertungen/umsatz/anmeldung-bestellung; bis 23.10.2026 „Von der
 * Anmeldung zur Bestellung" — „Anmeldung" heißt in P3 Webinar-Anmeldung). Umbau nach P2-V3 § 2.9: A1 vier Kacheln,
 * A2 je Anmeldequartal (Monate als ▸-Zeilen), A4 Einstieg nach Produkt | Kanal, A3 Dauer. EIN Schnitt am Datenstand
 * des Bestellsystems für Leads und Anfos. Veränderung der Leads zum Vorjahr erst nach Klärung des Rückgangs (F8,
 * Schalter `LEAD_VERAENDERUNG_ZEIGEN`). Netto.
 * Persona-Umbau C8 (28.10.2026): Hauptkachel ist der vertretbare Einkaufspreis je Lead (dahinter der Wert je Lead), die
 * Zahl der Leads steht klein; Befund zum Rückgang der neuen Leads je Quartal (Quartal gegen Quartal, kein Vorjahr — F8);
 * die Karte „Wie schnell sich neue Newsletter-Abonnenten zum Webinar anmelden" (bisher P1) steht hier als Teil des
 * Lead-Trichters (Datei `webinare-produkte/RegistrierungKarte.tsx` unverändert).
 */
import { FC, useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChapterPage, { useAuswertungenMeta } from '../../../components/auswertungen/ChapterPage';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { TipRow, useVizStyle, achse } from '../../../components/auswertungen/vizStyle';
import Explainer, { ExplainerSection } from '../../../components/Explainer';
import { breiteAus } from '../../../components/auswertungen/spaltenwahl';
import { balkenHoehe, BefundZeile, formatDelta, Hinweis, int, Kachel, KACHEL_RASTER, kompakt, Laden, nachGruppenReihenfolge, ProduktZelle, useBreitePx, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import RegistrierungKarte from '../webinare-produkte/RegistrierungKarte';
import { leadBefund } from './befund';
import { formatEur, formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { AnmeldungAntwortP2, AnmeldungMonatP2, AnmeldungQuartalP2, fetchAnmeldungBestellungP2, formatBerlinWallclock, formatMonat, formatPeriode } from '../../../helpers/auswertungenData';
import { letzterTag, P2_GRUPPEN, QuoteZelle, standText } from './teile';

/**
 * F8 (Till 23.10.2026): Veränderung der neuen Leads zum Vorjahr erst zeigen, wenn der Rückgang geklärt ist. Gemessen
 * 25.10.: alle Marken fallen seit Anfang 2026 ähnlich, ein Quell- oder Definitionswechsel nur bei FT ist nicht zu sehen
 * — geklärt ist die Ursache damit nicht. Die API rechnet die Veränderung schon; umschalten = diese Konstante.
 */
const LEAD_VERAENDERUNG_ZEIGEN = false;

const EXPLAINER: ExplainerSection[] = [
  {
    label: 'Datengrundlage',
    body: 'Neue Leads bei Finanztrends (erstes bestätigtes Opt-in für den Newsletter) und die Anfos derselben Adresse ab dem Anmeldetag. Gezählt werden dieselben Anfos wie überall in dieser Sektion (ohne Stornos und Testbestellungen). Leads und Anfos enden am selben Schnitt: dem Datenstand des Bestellsystems.',
  },
  {
    label: 'Berechnung',
    body: 'Quoten mit festem Fenster: Anfo binnen 30 bzw. 90 Tagen nach der Anmeldung, nur über Leads, die schon so lange dabei sind. Der Erwartete Wert je Lead ebenso: die Anfos binnen 90 Tagen, geteilt durch die Leads, die mindestens 90 Tage dabei sind — sonst drückten junge Leads den Schnitt. Bewertet wird mit dem heutigen Wert des Angebots jeder Anfo.',
  },
  {
    label: 'Wichtig',
    highlight: true,
    body: 'Alle Euro-Beträge netto (ohne MwSt.). Die alte Seite „Anfo-Statistik“ rechnet brutto (rund 6 % höher), zählt Testbestellungen mit und Euro-Werte ohne festes Fenster; ihre Zahlen weichen deshalb ab.',
  },
];

const fensterGrund = (n: number, quote: number) => `Weniger als ${formatPct(quote * 100, 0)} der Leads sind schon ${n} Tage dabei.`;

/** Quote „binnen n Tagen" mit „—", solange zu wenige Leads das Fenster erreicht haben (P2-V3 § 2.0/3). */
const BinnenZelle: FC<{ rate: number | null; z: number; reif: number; leads: number; tage: number; min: number }> = ({ rate, z, reif, leads, tage, min }) =>
  leads === 0 || reif / leads < min || rate === null ? (
    <Tooltip title={fensterGrund(tage, min)} arrow placement="top">
      <Box component="span" sx={{ cursor: 'help' }}>
        —
      </Box>
    </Tooltip>
  ) : (
    <Box sx={{ lineHeight: 1.25 }}>
      {formatPct(rate, 2)}
      <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
        {int(z)} von {int(reif)}
      </Box>
    </Box>
  );

const KachelnKarte: FC<{ d: AnmeldungAntwortP2 }> = ({ d }) => {
  const k = d.kennzahlen;
  const meta = useAuswertungenMeta();
  const leadsStand = meta?.sources.find((x) => x.key === 'leads_first_appearance')?.updated_at ?? null;
  const tm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
  const bisTag = d.schnitt ? d.schnitt.slice(0, 10) : d.window.bis;
  const min = d.fenster_min_anteil ?? 0.8;
  // Befund: nur volle Quartale; die 90-Tage-Quote nur dort, wo die Tabelle sie zeigt (genug Leads im Fenster).
  const laufendQ = (d.quartale ?? []).find((q) => d.leads_bis && d.leads_bis < quartalsEnde(q.quarter))?.quarter ?? null;
  const befund = leadBefund(
    (d.quartale ?? []).map((q) => ({ quarter: q.quarter, leads: q.leads, rate90: q.leads > 0 && q.mature90 / q.leads >= min ? q.rate90 : null })),
    laufendQ,
  );
  return (
    <ChartCard
      title="Wie viele neue Leads bestellen"
      howToRead="Die neuen Leads der letzten 12 Monate: was ein neuer Lead höchstens kosten darf (aus dem, was er in seinen ersten 90 Tagen erwarten lässt), welcher Anteil binnen 90 Tagen eine Anfo abgibt und wie oft die erste Anfo bezahlt wird."
      terms={[
        { label: TERMS.neueLeads.label, tip: TERMS.neueLeads.tip },
        { label: TERMS.anfoBinnen90.label, tip: TERMS.anfoBinnen90.tip },
        { label: TERMS.wertJeNeuemLead.label, tip: TERMS.wertJeNeuemLead.tip },
        { label: TERMS.einkaufspreisJeLead.label, tip: TERMS.einkaufspreisJeLead.tip },
        { label: TERMS.ersteAnfoBezahlt.label, tip: TERMS.ersteAnfoBezahlt.tip },
      ]}
      source="Neue Leads (Lead-System), PL-X-Bestellungen und Zahlungen, Anfo-Wert je Anfo"
      freshnessKey={['leads_first_appearance', 'plx_orders', 'anfo_werte']}
    >
      <BefundZeile text={'text' in befund ? befund.text : undefined} fehlt={'fehlt' in befund ? befund.fehlt : null} />
      <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1.5 }}>
        Leads: Stand {formatBerlinWallclock(leadsStand).slice(0, 17)} · Bestellungen: {standText(d.schnitt)} · gezählt bis {tm(bisTag)}
        {d.schnitt ? `, ${d.schnitt.slice(11, 16)}` : ''}
      </Box>
      <Box sx={KACHEL_RASTER}>
        <Kachel
          wert={formatEur(k.cpa_per_lead)}
          label={TERMS.einkaufspreisJeLead.label}
          tip={TERMS.einkaufspreisJeLead.tip}
          sub={
            <>
              {TERMS.wertJeNeuemLead.label}: <strong>{formatEur(k.expected_per_lead)}</strong> (Anfos der ersten 90 Tage)
              {d.cpa_anteil != null && `, davon ${formatPct(d.cpa_anteil * 100, 0)}`}
            </>
          }
        />
        <Kachel
          wert={formatPct(k.rate90, 2)}
          label={TERMS.anfoBinnen90.label}
          tip={TERMS.anfoBinnen90.tip}
          sub={
            <>
              {int(k.within90)} von {int(k.mature90)} Leads, die schon 90 Tage dabei sind · binnen 30 Tagen: {formatPct(k.rate30, 2)} ·{' '}
              <Tooltip title={TERMS.neueLeads.tip} arrow placement="top">
                <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                  {kompakt(k.leads)} neue Leads in 12 Monaten
                </Box>
              </Tooltip>
              {LEAD_VERAENDERUNG_ZEIGEN && k.veraenderung_leads != null
                ? ` (Veränderung zum Vorjahr ${formatDelta(k.veraenderung_leads, 0)})`
                : ' (Veränderung zum Vorjahr: wird geprüft)'}
            </>
          }
        />
        <Kachel
          wert={formatPct(k.first_pay_rate)}
          label={TERMS.ersteAnfoBezahlt.label}
          tip={TERMS.ersteAnfoBezahlt.tip}
          sub={`${int(k.first_paid)} von ${int(k.first_mature)} ersten Anfos, die schon ${int(d.reife_tage ?? 60)} Tage alt sind`}
        />
      </Box>
      <Hinweis>
        Euro netto (ohne MwSt.), bewertet mit dem heutigen Wert der Angebote ({d.anfo_wert_stand}). Alle Werte zählen die Anfos der ersten 90
        Tage. Werte von vor dem 08.10.2026 (alte Seite Anfo-Statistik, frühere Exporte) zählten alle Anfos bis heute und sind nicht vergleichbar.
      </Hinweis>
    </ChartCard>
  );
};

type QZeile = (AnmeldungQuartalP2 | (AnmeldungMonatP2 & { quarter?: undefined })) & { key: string; kinderZeilen?: QZeile[] };

const STANDARDS_A2: Record<Breite, readonly string[]> = {
  breit: ['q', 'leads', 'r90', 'wert', 'r30'],
  mittel: ['q', 'leads', 'r90', 'wert'],
  schmal: ['q', 'r90', 'wert'],
};

const QuartaleKarte: FC<{ d: AnmeldungAntwortP2 }> = ({ d }) => {
  const viz = useVizStyle();
  const theme = useTheme();
  const breit = useMediaQuery(theme.breakpoints.up('md'));
  const min = d.fenster_min_anteil ?? 0.8;
  const zeilen: QZeile[] = useMemo(
    () =>
      [...(d.quartale ?? [])].reverse().map((q) => ({
        ...q,
        key: q.quarter,
        kinderZeilen: [...q.months].reverse().map((m) => ({ ...m, key: m.month, quarter: undefined })),
      })),
    [d],
  );
  const chartData = useMemo(() => d.verlauf.map((m) => ({ month: m.month, leads: m.leads })), [d]);
  const name = (r: QZeile) => (r.quarter ? formatPeriode(r.quarter) : formatMonat((r as AnmeldungMonatP2).month));
  const laufend = (r: QZeile) => r.quarter && d.leads_bis && d.leads_bis < quartalsEnde(r.quarter);

  const baue = (sichtbar: ReadonlySet<string>): Spalte<QZeile>[] => [
    {
      key: 'q',
      label: TERMS.anmeldequartal.label,
      tip: TERMS.anmeldequartal.tip,
      gruppe: 'Menge',
      pflicht: true,
      render: (r) => (
        <Box component="span" sx={{ lineHeight: 1.3 }}>
          {name(r)}
          {laufend(r) && (
            <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: 'text.secondary' }}>
              {/* Letzter GEZÄHLTER Tag: `leads_bis` ist das exklusive Ende (Persona-Check 26.10.2026, B11/B37). */}
              bis {letzterTag(d.leads_bis ?? '').slice(8, 10)}.{letzterTag(d.leads_bis ?? '').slice(5, 7)}.
            </Box>
          )}
          {!sichtbar.has('leads') && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
              {int(r.leads)} Leads
            </Box>
          )}
        </Box>
      ),
    },
    { key: 'leads', label: TERMS.neueLeads.label, tip: TERMS.neueLeads.tip, gruppe: 'Menge', align: 'right', render: (r) => int(r.leads) },
    ...(LEAD_VERAENDERUNG_ZEIGEN
      ? [
          {
            key: 'veraenderung',
            label: 'Veränderung zum Vorjahresquartal',
            gruppe: 'Trend',
            align: 'right' as const,
            render: (r: QZeile) => (r.quarter ? formatDelta((r as AnmeldungQuartalP2).veraenderung_leads ?? null, 1) : null),
          },
        ]
      : []),
    {
      key: 'r90',
      label: TERMS.anfoBinnen90.label,
      tip: TERMS.anfoBinnen90.tip,
      gruppe: 'Zahlung',
      pflicht: true,
      align: 'right',
      render: (r) => <BinnenZelle rate={r.rate90} z={r.within90} reif={r.mature90} leads={r.leads} tage={90} min={min} />,
    },
    {
      key: 'wert',
      label: TERMS.wertJeNeuemLead.label,
      tip: TERMS.wertJeNeuemLead.tip,
      einheit: TERMS.wertJeNeuemLead.einheit,
      gruppe: 'Wert',
      pflicht: true,
      align: 'right',
      render: (r) =>
        r.leads === 0 || r.mature90 / r.leads < min ? (
          <Tooltip title={fensterGrund(90, min)} arrow placement="top">
            <Box component="span" sx={{ cursor: 'help' }}>
              —
            </Box>
          </Tooltip>
        ) : (
          formatEur(r.expected_per_lead)
        ),
    },
    {
      key: 'r30',
      label: TERMS.anfoBinnen30.label,
      tip: TERMS.anfoBinnen30.tip,
      gruppe: 'Zahlung',
      align: 'right',
      render: (r) => <BinnenZelle rate={r.rate30} z={r.within30} reif={r.mature30} leads={r.leads} tage={30} min={min} />,
    },
    {
      key: 'summe',
      label: TERMS.wertAusLeads.label,
      tip: TERMS.wertAusLeads.tip,
      einheit: TERMS.wertAusLeads.einheit,
      gruppe: 'Wert',
      align: 'right',
      render: (r) => (r.mature90 / Math.max(r.leads, 1) < min ? '—' : formatEur(r.expected_sum ?? null, 0)),
    },
    { key: 'mit', label: TERMS.mitAnfoBisHeute.label, tip: TERMS.mitAnfoBisHeute.tip, gruppe: 'Zahlung', align: 'right', render: (r) => int(r.with_anfo) },
  ];
  const meta = nachGruppenReihenfolge(spaltenMeta(baue(new Set())), P2_GRUPPEN);
  const wahl = useSpaltenwahl('leadsQuartal', meta, STANDARDS_A2);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(sichtbar).filter((c) => sichtbar.has(c.key));

  return (
    <ChartCard
      title="Je Anmeldequartal"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead="Je Quartal, in dem sich jemand neu angemeldet hat: wie viele Leads es waren, welcher Anteil binnen 90 Tagen eine Anfo abgegeben hat und was ein Lead in seinen ersten 90 Tagen erwarten lässt. Die Monate stehen aufklappbar darunter. Wo noch zu wenige Leads 90 Tage dabei sind, steht „—“."
      terms={[
        { label: TERMS.anfoBinnen90.label, tip: TERMS.anfoBinnen90.tip },
        { label: TERMS.anfoBinnen30.label, tip: TERMS.anfoBinnen30.tip },
        { label: TERMS.wertJeNeuemLead.label, tip: TERMS.wertJeNeuemLead.tip },
      ]}
      source="Neue Leads (Lead-System), PL-X-Bestellungen, Anfo-Wert je Anfo"
      freshnessKey={['leads_first_appearance', 'plx_orders', 'anfo_werte']}
    >
      <Box ref={wahl.messRef}>
        <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>Neue Leads je Monat</Box>
        <Box sx={{ height: 200 }}>
          <ResponsiveBar
            data={chartData as unknown as BarDatum[]}
            keys={['leads']}
            indexBy="month"
            margin={{ top: 8, right: 8, bottom: 32, left: 52 }}
            padding={0.35}
            borderRadius={2}
            colors={[viz.series1]}
            theme={viz.nivo}
            animate={false}
            enableLabel={false}
            enableGridY
            gridYValues={4}
            axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 4, format: achse('') }}
            axisBottom={{
              tickSize: 0,
              tickPadding: 6,
              tickValues: chartData.map((x) => x.month).filter((_, i, a) => (a.length - 1 - i) % (breit ? 3 : 6) === 0),
              format: (v) => formatMonat(String(v)),
            }}
            tooltip={({ indexValue, data: x }) => (
              <Box sx={{ ...(viz.nivo.tooltip as { container: object }).container, px: 1.5, py: 1 }}>
                <TipRow color={viz.series1} label={formatMonat(String(indexValue))} value={`${int(Number(x.leads))} ${TERMS.neueLeads.label}`} />
              </Box>
            )}
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <SortableTable
            rows={zeilen}
            columns={spalten}
            rowKey={(r) => r.key}
            kinder={(r) => (r.kinderZeilen && r.kinderZeilen.length > 1 ? r.kinderZeilen : undefined)}
            kinderText={(r) => `${int(r.kinderZeilen?.length ?? 0)} Monate`}
            maxHeight={null}
            eng={wahl.breite === 'schmal'}
          />
        </Box>
        <Hinweis>
          „—“: weniger als {formatPct(min * 100, 0)} der Leads haben das Fenster schon erreicht. Die Veränderung zum Vorjahr zeigt die Seite erst,
          wenn der Rückgang der neuen Leads geklärt ist.
        </Hinweis>
      </Box>
    </ChartCard>
  );
};

/** `2026-Q4` → `2027-01-01` (exklusives Ende). */
const quartalsEnde = (q: string): string => {
  const [j, n] = q.split('-Q').map(Number);
  return n === 4 ? `${j + 1}-01-01` : `${j}-${String(n * 3 + 1).padStart(2, '0')}-01`;
};

type EinstiegZeile = AnmeldungAntwortP2['produkte'][number] | NonNullable<AnmeldungAntwortP2['kanaele']>[number];

const EinstiegKarte: FC<{ d: AnmeldungAntwortP2 }> = ({ d }) => {
  const [nach, setNach] = useState<'produkt' | 'kanal'>('produkt');
  const zeilen: EinstiegZeile[] = nach === 'produkt' ? d.produkte : d.kanaele ?? [];
  const alle = d.produkte.reduce((s, p) => ({ n: s.n + p.n, mature: s.mature + p.mature, paid: s.paid + p.paid }), { n: 0, mature: 0, paid: 0 });
  // Schmal (< 640 px Karte) stehen Anzahl, Anteil und Ø Wert als Unterzeile am Namen: fünf Spalten liefen bei 267 und
  // 414 px waagrecht über (Abnahme 25.10.2026). Keine Spaltenwahl (P2-V3 § 5).
  const [breiteRef, breitePx] = useBreitePx();
  const schmal = breitePx !== null && breiteAus(breitePx) === 'schmal';
  const unter = (r: EinstiegZeile) => `${int(r.n)} · ${formatPct(r.share)} · Ø ${formatEur(r.expected_value, 0)}`;
  const spalten: Spalte<EinstiegZeile>[] = [
    {
      key: 'p',
      label: nach === 'produkt' ? 'Produkt der ersten Anfo' : TERMS.kanalErsteAnfo.label,
      tip: nach === 'kanal' ? TERMS.kanalErsteAnfo.tip : undefined,
      render: (r) => (
        <Box component="span" sx={{ lineHeight: 1.3 }}>
          {'plx_product' in r ? (
            <ProduktZelle code={r.plx_product || '(ohne Artikel)'} name={r.einmalkauf && r.name ? `${r.name} (Einmalkauf)` : r.name} />
          ) : (
            r.label
          )}
          {schmal && (
            <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
              {unter(r)}
            </Box>
          )}
        </Box>
      ),
      sortValue: (r) => ('plx_product' in r ? r.plx_product : r.label),
      summary: schmal ? `Alle · ${int(alle.n)}` : 'Alle',
    },
    ...((schmal ? [] : [
    { key: 'n', label: TERMS.ersteAnfos.label, tip: TERMS.ersteAnfos.tip, align: 'right', render: (r) => int(r.n), sortValue: (r) => r.n, summary: int(alle.n) },
    { key: 's', label: TERMS.anteilErsteAnfos.label, tip: TERMS.anteilErsteAnfos.tip, align: 'right', render: (r) => formatPct(r.share), sortValue: (r) => r.share, summary: '100 %' },
    { key: 'w', label: TERMS.wertErsteAnfo.label, tip: TERMS.wertErsteAnfo.tip, align: 'right', render: (r) => formatEur(r.expected_value, 0), sortValue: (r) => r.expected_value },
    ]) as Spalte<EinstiegZeile>[]),
    {
      key: 'q',
      label: TERMS.ersteAnfoBezahlt.label,
      tip: TERMS.ersteAnfoBezahlt.tip,
      align: 'right',
      render: (r) => <QuoteZelle rate={r.pay_rate} bezahlt={r.paid} reif={r.mature} />,
      sortValue: (r) => r.pay_rate,
      summary: <QuoteZelle rate={alle.mature > 0 ? (alle.paid / alle.mature) * 100 : null} bezahlt={alle.paid} reif={alle.mature} />,
    },
  ];
  return (
    <ChartCard
      title="Womit neue Leads einsteigen"
      actions={
        <ToggleButtonGroup size="small" exclusive value={nach} onChange={(_, v) => v && setNach(v)} aria-label="Einstieg nach">
          <ToggleButton value="produkt" sx={{ textTransform: 'none', py: 0.25 }}>
            nach Produkt
          </ToggleButton>
          <ToggleButton value="kanal" sx={{ textTransform: 'none', py: 0.25 }}>
            nach Kanal
          </ToggleButton>
        </ToggleButtonGroup>
      }
      howToRead="Die erste Anfo der neuen Leads aus den 12 Monaten: welches Produkt bzw. welcher Kanal, was sie erwarten lässt und wie oft sie bezahlt wurde. Unter 20 reifen ersten Anfos steht die Quote grau."
      terms={[
        { label: TERMS.ersteAnfos.label, tip: TERMS.ersteAnfos.tip },
        { label: TERMS.wertErsteAnfo.label, tip: TERMS.wertErsteAnfo.tip },
        { label: TERMS.ersteAnfoBezahlt.label, tip: TERMS.ersteAnfoBezahlt.tip },
      ]}
      source="Neue Leads (Lead-System), PL-X-Bestellungen und Zahlungen, Anfo-Wert je Anfo"
      freshnessKey={['leads_first_appearance', 'plx_orders']}
    >
      <Box ref={breiteRef}>
        <SortableTable
          rows={zeilen}
          columns={spalten}
          rowKey={(r) => ('plx_product' in r ? r.plx_product || '-' : r.channel)}
          initialSort={{ key: 'n', dir: 'desc' }}
          maxHeight={null}
          eng={schmal}
        />
      </Box>
    </ChartCard>
  );
};

const VerteilungKarte: FC<{ d: AnmeldungAntwortP2 }> = ({ d }) => {
  const viz = useVizStyle();
  const breit = useMediaQuery(useTheme().breakpoints.up('md'));
  const v = d.verteilung;
  // Nivo legt die erste Zeile nach unten: umgedreht, damit „am selben Tag" oben steht.
  const chartData = useMemo(() => [...v.eimer].reverse().map((e) => ({ label: e.label, share: e.share ?? 0, n: e.n })), [v]);
  const spaet = v.eimer.find((e) => e.key === 'd181');
  return (
    <ChartCard
      title="Wie lange es bis zur ersten Anfo dauert"
      howToRead={`Von den neuen Leads mit Anfo: wie viele Tage zwischen Anmeldung und erster Anfo lagen. Gezählt nur Leads, die mindestens ${v.reife_tage} Tage dabei sind — jüngere können lange Abstände noch gar nicht haben.`}
      terms={[{ label: TERMS.neueLeads.label, tip: TERMS.neueLeads.tip }]}
      source="Neue Leads (Lead-System), PL-X-Bestellungen"
      freshnessKey={['leads_first_appearance', 'plx_orders']}
    >
      <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>Neue Leads, die mindestens {v.reife_tage} Tage dabei sind</Box>
      <Box sx={{ height: balkenHoehe(chartData.length) }}>
        <ResponsiveBar
          data={chartData as unknown as BarDatum[]}
          keys={['share']}
          indexBy="label"
          layout="horizontal"
          margin={{ top: 4, right: 64, bottom: 32, left: 112 }}
          padding={0.3}
          borderRadius={2}
          colors={[viz.series1]}
          theme={viz.nivo}
          animate={false}
          enableLabel={false}
          enableGridY={false}
          enableGridX
          axisLeft={{ tickSize: 0, tickPadding: 8 }}
          axisBottom={{ tickSize: 0, tickPadding: 6, tickValues: breit ? 4 : 2, format: achse('%') }}
          tooltip={({ indexValue, data: x }) => (
            <Box sx={{ ...(viz.nivo.tooltip as { container: object }).container, px: 1.5, py: 1 }}>
              <TipRow color={viz.series1} label={String(indexValue)} value={`${formatPct(Number(x.share))} (${int(Number(x.n))})`} />
            </Box>
          )}
          layers={[
            'grid',
            'axes',
            'bars',
            ({ bars }) => (
              <g>
                {bars.map((b) => (
                  <text key={b.key} x={b.x + b.width + 6} y={b.y + b.height / 2} dominantBaseline="central" fontSize={12} fontFamily={viz.fontFamily} fill={viz.inkSecondary}>
                    {formatPct(Number(b.data.value), 0)}
                  </text>
                ))}
              </g>
            ),
          ]}
        />
      </Box>
      <Hinweis>
        Von den Leads, die bestellen, bestellt die Hälfte binnen {v.median_days === null ? '–' : `${int(v.median_days)} Tagen`}
        {spaet ? `; ${formatPct(spaet.share, 0)} erst nach über 180 Tagen` : ''}. Grundlage: {int(v.with_anfo)} Leads mit Anfo von {int(v.leads)} Leads,
        die mindestens {v.reife_tage} Tage dabei sind.
      </Hinweis>
    </ChartCard>
  );
};

const AnmeldungBestellung: FC = () => {
  const { data, fehler } = useLaden(fetchAnmeldungBestellungP2);
  return (
    <ChapterPage
      title="Vom neuen Lead zur Bestellung"
      question="Wie viele neue Newsletter-Leads bestellen, wie schnell, womit — und was ist ein neuer Lead wert?"
      zurueck={{ to: '/auswertungen/umsatz', label: 'Umsatz & Bezahlqualität' }}
      standQuellen={[
        { key: 'leads_first_appearance', label: 'Neue Leads' },
        { key: 'plx_orders', label: 'Bestellsystem (PL-X)' },
        { key: 'anfo_werte', label: 'Anfo-Werte' },
      ]}
    >
      <Explainer title="Wie diese Seite rechnet" sections={EXPLAINER} />
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          <KachelnKarte d={data} />
          <QuartaleKarte d={data} />
          {/* C8: Lead → erste Webinar-Anmeldung, derselbe Trichter (Karte aus P1, Datei unverändert, eigener Abruf). */}
          <RegistrierungKarte />
          <EinstiegKarte d={data} />
          <VerteilungKarte d={data} />
        </>
      )}
    </ChapterPage>
  );
};

export default AnmeldungBestellung;
